import type { LiceCurrent } from '../api'
import { useT } from '../lang'
import { Louse, nb } from './Varsel'

/* PER TI FISK — the one number, made countable.
 *
 * WHY 0,42 IS A BAD SENTENCE AND FOUR LICE IS A GOOD ONE.
 *
 * "0,42 voksne hunnlus per fisk" is a mean of a right-skewed distribution
 * expressed as a decimal fraction of an animal, and there is no such thing as
 * four tenths of a louse. Everybody in this industry can recite the number and
 * almost nobody can picture it, which is why the question that actually gets
 * asked on a walkway is never "what is the figure" but "how close are we".
 *
 * Otto Neurath's museum in Vienna, from 1925, existed to answer exactly this
 * for exactly this kind of reader: working people, standing up, in passing,
 * with no statistical training and better things to do. The Vienna Method —
 * later Isotype — has one central rule, and it is the rule this file is built
 * on:
 *
 *     A GREATER QUANTITY IS SHOWN BY MORE SIGNS, NEVER BY A BIGGER SIGN.
 *
 * So: ten fish, and the lice that are on them. 0,42 per fish is four lice
 * among ten fish, which is a true restatement of a mean and not a rounding of
 * it. The limit is a rule drawn through the same frame. Being over is then not
 * a colour or a word or a chip — it is signs standing past a line, which is
 * the one comparison a person can make without being taught anything.
 *
 * WHAT THIS DELIBERATELY DOES NOT SAY.
 *
 * NOT "four fish in ten have a louse". Lice are overdispersed across a
 * population — a few fish carry many — so that sentence is false at any mean,
 * and it is the sentence a reader invents if you draw ten fish and shade four
 * of them. So the fish are not drawn at all. The frame is ten slots, the
 * caption says "per ti fisk", and every sign in it is a louse.
 *
 * THE UNIT IS FIXED AT TEN FISH AND DOES NOT ADAPT. It is tempting to rescale
 * for a farm at 0,04 so that something shows. But the limit has to stay inside
 * the frame or the whole comparison is gone, and the limits this domain uses —
 * 0,50, 0,20, 0,10 — all land on a whole slot at ten fish and none of them
 * does at a hundred. A farm under half a louse per ten fish showing an EMPTY
 * frame is not a failure of the drawing. It is the answer.
 *
 * THE RED IS THE EXCESS, NOT THE STATE. Signs before the rule are --text;
 * signs past it are --alert-text. So red is never a verdict stamped on a row —
 * it is the amount by which the farm is over, drawn at the size it actually
 * is, and one louse over the line looks like one louse over the line. This is
 * the same idea `Syklus` uses for the area between the band and the limit, and
 * it is deliberately the same idea in both places.
 */

/** Signs per fish. Ten, and see the header for why it never changes. */
const FISK = 10

/** Where the frame stops growing. Above this the row would wrap into a wall of
 *  lice that nobody counts and everybody just reads as "a lot" — at which
 *  point the signs have stopped being countable and Neurath's rule is broken
 *  in the other direction. The number beside them is still exact. */
const MAKS = 24

export function PerTiFisk({ cur }: { cur: LiceCurrent }) {
  const t = useT()
  const v = cur.adult_female
  const grense = cur.threshold

  /* Rounded to whole signs, because a sign is one louse and there are no
     fractions of one. The exact figure is printed alongside, so the rounding
     costs a reader nothing they cannot immediately recover. */
  const lus = v === null ? null : Math.round(v * FISK)
  const strek = grense === null ? null : Math.round(grense * FISK)

  if (lus === null) {
    return (
      <div className="tifisk">
        <p className="tifisk-svar">{t('Nothing counted here yet.')}</p>
      </div>
    )
  }

  const vist = Math.min(lus, MAKS)
  /* The frame is as wide as it needs to be: the limit, the count, or the run
     of ten the unit is named after — whichever is furthest out. A frame that
     ended at the limit would clip exactly the farms that need to see the
     overshoot. */
  const slots = Math.max(FISK, vist, strek ?? 0)
  const over = strek !== null && lus >= strek

  return (
    <div className="tifisk">
      {/* ONE role="img" OVER THE WHOLE FRAME. A screen reader must never be
          made to count twenty-two identical children to arrive at a number
          that is written in words right here. */}
      <div className="tifisk-ramme" role="img"
        aria-label={`${lus} ${t('lice per ten fish')}${
          strek !== null ? `, ${t('the limit is')} ${strek}` : ''}`}>
        {Array.from({ length: slots }, (_, i) => (
          <span key={i}
            className={`tifisk-plass${
              /* AT OR OVER IS A BREACH — `antallFor` is the API's rule and
                 it reads `verdi >= grense`. Slot i holds the (i+1)-th louse,
                 so the first sign that puts a farm in breach is the one at
                 index strek-1, and that is where the rule goes. A farm at
                 exactly the limit shows its last sign PAST the rule, which is
                 the truth: five lice per ten fish against a limit of five is
                 over. Drawing the rule one slot later would make the picture
                 disagree with the regulation, quietly, only at the boundary. */
              strek !== null && i === strek - 1 ? ' grense' : ''}${
              i >= vist ? ' tom'
                : i >= (strek ?? Infinity) - 1 ? ' over' : ''}`}>
            {i < vist && <Louse />}
          </span>
        ))}
        {lus > MAKS && <span className="tifisk-mer">+{lus - MAKS}</span>}
      </div>

      {/* THE SENTENCE, IN THE SERIF. docs/not-generic.md puts Spectral on what
          people read to decide, and the decision on this page is made off this
          line. The count leads because it is the answer; the decimal follows
          because it is the evidence and it is what gets typed into a form. */}
      <p className="tifisk-svar">
        <strong className={over ? 'over' : undefined}>
          {lus} {t('lice per ten fish')}
        </strong>
        <span className="faint">
          {' · '}{nb(v!)}{' '}{t('per fish')}
          {grense !== null && <>{', '}{t('limit')} {nb(grense)}</>}
        </span>
      </p>

      {/* Where it came from and when. Not a chip and not a verdict — the
          verdict is drawn above, and saying it again in a word would be the
          third rendering of one fact. */}
      <p className="tifisk-kilde faint">
        {cur.source === 'own_count'
          ? `${t('counted at the farm')} ${cur.as_of ?? ''}${
            cur.counted_by ? ` · ${cur.counted_by}` : ''}`
          : `${t('reported to the register')} ${cur.as_of ?? ''}`}
      </p>
    </div>
  )
}
